import React, { useState, useEffect } from 'react';
import externalMcpServersService from '../services/externalMcpServersService';
import './ExternalMcpServers.css';

function ExternalMcpServers({ isOpen, onClose }) {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newServerName, setNewServerName] = useState('');
  const [newMcpUrl, setNewMcpUrl] = useState('');
  const [newDiscoveryUrl, setNewDiscoveryUrl] = useState('');
  const [newAllowedClientIds, setNewAllowedClientIds] = useState('');
  const [addingServer, setAddingServer] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadServers();
    }
  }, [isOpen]);

  const loadServers = async () => {
    setLoading(true);
    setError('');
    try {
      const fetchedServers = await externalMcpServersService.getExternalMcpServers();
      setServers(fetchedServers);
    } catch (err) {
      setError('Failed to load external MCP servers: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddServer = async () => {
    if (!newServerName.trim() || !newMcpUrl.trim() || !newDiscoveryUrl.trim()) {
      setError('Server name, MCP URL, and Discovery URL are required');
      return;
    }

    // Parse allowed client IDs from comma-separated string
    const allowedClientIds = newAllowedClientIds
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await externalMcpServersService.putExternalMcpServer(
        newServerName.trim(),
        newMcpUrl.trim(),
        newDiscoveryUrl.trim(),
        allowedClientIds
      );
      setSuccess('External MCP server added successfully!');
      setNewServerName('');
      setNewMcpUrl('');
      setNewDiscoveryUrl('');
      setNewAllowedClientIds('');
      setAddingServer(false);
      await loadServers();
    } catch (err) {
      setError('Failed to add external MCP server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteServer = async (serverName) => {
    if (!window.confirm(`Are you sure you want to delete the external MCP server "${serverName}"?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    try {
      await externalMcpServersService.deleteExternalMcpServer(serverName);
      setSuccess('External MCP server deleted successfully!');
      await loadServers();
    } catch (err) {
      setError('Failed to delete external MCP server: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="external-mcp-servers-modal-overlay" onClick={onClose}>
      <div className="external-mcp-servers-modal" onClick={(e) => e.stopPropagation()}>
        <div className="external-mcp-servers-header">
          <h2>🌐 External MCP Servers</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="external-mcp-servers-content">
          {error && <div className="external-mcp-servers-error">{error}</div>}
          {success && <div className="external-mcp-servers-success">{success}</div>}

          <div className="external-mcp-servers-section">
            <div className="external-mcp-servers-section-header">
              <h3>Your External MCP Servers</h3>
              <button 
                className="add-server-button"
                onClick={() => setAddingServer(!addingServer)}
                disabled={loading}
              >
                {addingServer ? '✖ Cancel' : '➕ Add Server'}
              </button>
            </div>

            {addingServer && (
              <div className="add-server-form">
                <div className="form-group">
                  <label>Server Name:</label>
                  <input
                    type="text"
                    value={newServerName}
                    onChange={(e) => setNewServerName(e.target.value)}
                    placeholder="e.g., my-mcp-server"
                    className="server-input"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>MCP URL:</label>
                  <input
                    type="text"
                    value={newMcpUrl}
                    onChange={(e) => setNewMcpUrl(e.target.value)}
                    placeholder="e.g., https://mcp.example.com/v1"
                    className="server-input"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Discovery URL:</label>
                  <input
                    type="text"
                    value={newDiscoveryUrl}
                    onChange={(e) => setNewDiscoveryUrl(e.target.value)}
                    placeholder="e.g., https://mcp.example.com/discovery"
                    className="server-input"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Allowed Client IDs (comma-separated):</label>
                  <input
                    type="text"
                    value={newAllowedClientIds}
                    onChange={(e) => setNewAllowedClientIds(e.target.value)}
                    placeholder="e.g., client1, client2, client3"
                    className="server-input"
                    disabled={loading}
                  />
                  <small className="input-hint">Leave empty to allow all clients</small>
                </div>
                <button 
                  className="save-server-button"
                  onClick={handleAddServer}
                  disabled={loading || !newServerName.trim() || !newMcpUrl.trim() || !newDiscoveryUrl.trim()}
                >
                  {loading ? '⏳ Saving...' : '💾 Save Server'}
                </button>
              </div>
            )}

            <div className="servers-list">
              {loading && servers.length === 0 ? (
                <div className="servers-loading">⏳ Loading servers...</div>
              ) : servers.length === 0 ? (
                <div className="servers-empty">
                  <p>No external MCP servers found.</p>
                  <p>Click "Add Server" to configure your first external MCP server.</p>
                </div>
              ) : (
                servers.map((server) => (
                  <div key={server.serverName} className="server-item">
                    <div className="server-item-header">
                      <div className="server-name">🖥️ {server.serverName}</div>
                      <button
                        className="delete-server-button"
                        onClick={() => handleDeleteServer(server.serverName)}
                        disabled={loading}
                        title="Delete server"
                      >
                        🗑️
                      </button>
                    </div>
                    <div className="server-details">
                      <div className="server-detail-row">
                        <span className="server-detail-label">MCP URL:</span>
                        <code className="server-detail-value">{server.mcpUrl}</code>
                      </div>
                      <div className="server-detail-row">
                        <span className="server-detail-label">Discovery URL:</span>
                        <code className="server-detail-value">{server.discoveryUrl}</code>
                      </div>
                      <div className="server-detail-row">
                        <span className="server-detail-label">Allowed Client IDs:</span>
                        <code className="server-detail-value">
                          {server.allowedClientIds && server.allowedClientIds.length > 0
                            ? server.allowedClientIds.join(', ')
                            : 'All clients allowed'}
                        </code>
                      </div>
                    </div>
                    {server.updatedAt && (
                      <div className="server-metadata">
                        Last updated: {new Date(server.updatedAt).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="servers-info">
            <h4>ℹ️ About External MCP Servers</h4>
            <ul>
              <li>External MCP servers allow you to connect to third-party MCP services</li>
              <li>Each server requires an MCP URL and a Discovery URL</li>
              <li>You can optionally specify allowed client IDs for access control</li>
              <li>Servers are stored per-user and only accessible by you</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ExternalMcpServers;
